import * as React from 'react';

import { TeamRole } from '@showbie-socrative/socrative-utils';
import { PlanType } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';

import { Badge } from '~shared/components/Badge';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';

import { Setting } from './components/Setting';

export interface Props {
  currentPlan: PlanType;
  canViewEmail: Array<TeamRole>;
  setCanViewEmail: (newValue: Array<TeamRole>) => void;
  canChangeCanViewEmail: boolean;
}

export function CanViewEmailSetting({
  currentPlan,
  canViewEmail,
  setCanViewEmail,
  canChangeCanViewEmail,
}: Props) {
  const isOrg = [PlanType.Organization, PlanType.Complete].includes(
    currentPlan
  );

  const isToggled = getToggleStateFromRoles(new Set(canViewEmail));

  return (
    <Setting
      name={
        <Setting.Name>
          {translate('Hide Team Member Emails')}
          {!isOrg ? (
            <div className="flex flex-col justify-center">
              <Badge tone="pro">ORG</Badge>
            </div>
          ) : null}
        </Setting.Name>
      }
      description={
        <Setting.Description>
          {translate(
            'Member email addresses are hidden, but visible to Admins.'
          )}
        </Setting.Description>
      }
    >
      <Setting.Control>
        <div className="grid items-center justify-items-end flex-grow">
          <ToggleSwitch
            disabled={!isOrg || !canChangeCanViewEmail}
            size="small"
            on={isToggled}
            onChange={(checked) => {
              if (!canChangeCanViewEmail) {
                return;
              }

              const roles = Array.from(getRolesFromToggleState(checked));
              setCanViewEmail(roles);
            }}
          />
        </div>
      </Setting.Control>
    </Setting>
  );
}

const ADMIN_ONLY_ROLES = new Set([TeamRole.Owner, TeamRole.Admin]);
const ALL_ROLES = new Set([TeamRole.Owner, TeamRole.Admin, TeamRole.Member]);

// Helper function to serialize a set of roles consistently
function serializeRoleSet(roles: Set<TeamRole>): string {
  return Array.from(roles).sort().join(',');
}

// Create a mapping from serialized sets to boolean values
const roleSetToToggleMap = new Map<string, boolean>([
  [serializeRoleSet(ADMIN_ONLY_ROLES), true],
  [serializeRoleSet(ALL_ROLES), false],
]);

// Function to get toggle state from roles
function getToggleStateFromRoles(roles: Set<TeamRole>): boolean {
  const serialized = serializeRoleSet(roles);
  return roleSetToToggleMap.get(serialized) ?? false;
}

// Function to get roles from toggle state
function getRolesFromToggleState(isToggled: boolean): Set<TeamRole> {
  return isToggled ? ADMIN_ONLY_ROLES : ALL_ROLES;
}
